import React from 'react';
import './MapLegend.css';

// Reusable Legend component for different map variables
export interface LegendConfig {
  title: string;
  dataYear: string;
  colorScale: string[];
  breaks: number[];
  unit: string;
  tractCount: number;
  referenceValue?: { label: string; value: number };
}

const MapLegend: React.FC<LegendConfig> = ({
  title, 
  dataYear, 
  colorScale, 
  breaks, 
  unit, 
  tractCount, 
  referenceValue 
}) => {
  // For carbon footprint, show unit only in the header, not on every value
  const showUnitInTitleOnly = unit === ' MT CO₂e/HH';

  // Helper function to format political lean as "D +15" or "R +2"
  const formatPoliticalLean = (value: number): string => {
    if (value > 0) {
      return `D +${value.toFixed(1)}`;
    } else if (value < 0) {
      return `R +${Math.abs(value).toFixed(1)}`;
    } else {
      return 'Even';
    }
  };

  // Generate legend items dynamically based on breaks and colors
  const legendItems = breaks.slice(0, -1).map((breakValue, index) => {
    const nextBreak = breaks[index + 1];
    const color = colorScale[index];
    
    // Format numbers based on unit type
    const formatValue = (value: number) => {
      if (unit === ' MT CO₂e/HH') {
        return value.toFixed(1); // One decimal place for carbon footprint
      } else if (title === 'Political Lean') {
        // Special formatting for political lean only
        return formatPoliticalLean(value);
      } else {
        // For empty units or other units, use standard number formatting
        return value.toLocaleString();
      }
    };
    
    const unitSuffix = !showUnitInTitleOnly && unit ? unit : '';
    let label = '';
    if (title === 'Political Lean') {
      // Special formatting for political lean legend only
      if (index === 0) {
        label = `< ${formatValue(breakValue)}`;
      } else if (index === breaks.length - 2) {
        label = `${formatValue(breakValue)}+`;
      } else {
        label = `${formatValue(breakValue)} - ${formatValue(nextBreak)}`;
      }
      } else {
        // Standard formatting for other variables (including those with empty units)
        if (index === 0) {
          label = `Under ${formatValue(breakValue)}${unitSuffix}`;
        } else if (index === breaks.length - 2) {
          label = `${formatValue(breakValue)}${unitSuffix}+`;
        } else {
          label = `${formatValue(breakValue)}${unitSuffix} - ${formatValue(nextBreak)}${unitSuffix}`;
        }
      }
    
    return { color, label };
  });

  return (
    <div className="map-legend-root">
        <div className="map-legend-header">
          <h4 className="map-legend-title">
            {title}
            {showUnitInTitleOnly && ` (${unit.trim()})`}
          </h4>
          <p className="map-legend-year">{dataYear}</p>
        </div>
        
        <div className="map-legend-items">
          {legendItems.map((item, index) => (
            <div key={index} className="map-legend-item">
              <div
                className="map-legend-swatch"
                style={{ backgroundColor: item.color }}
              />
              <span className="map-legend-label">{item.label}</span>
            </div>
          ))}
        </div>
        
        <div className="map-legend-footer">
          {referenceValue && (
            <div>
              {referenceValue.label}: {
                title === 'Political Lean'
                  ? (referenceValue.value > 0 
                      ? `D +${referenceValue.value.toFixed(1)}`
                      : referenceValue.value < 0
                      ? `R +${Math.abs(referenceValue.value).toFixed(1)}`
                      : 'Even')
                  : unit === ' min'
                  ? `${referenceValue.value}${unit}`
                  : unit === ' MT CO₂e/HH'
                  ? `${referenceValue.value.toFixed(1)}${unit}`
                  : unit === '%'
                  ? `${referenceValue.value.toFixed(1)}${unit}`
                  : unit === ''
                  ? `${referenceValue.value.toLocaleString()}`
                  : `${referenceValue.value.toLocaleString()}${unit}`
              }
            </div>
          )}
          <div>{tractCount.toLocaleString()} census tracts</div>
          <div className="map-legend-divider">
            <div className="map-legend-nodata">
              <div className="map-legend-swatch" />
              <span>Gray = No data</span>
            </div>
          </div>
        </div>
    </div>
  );
};

export default MapLegend;
